import { get, max } from "lodash"
import { createSelector } from "reselect"
import moment from "moment"
import { createQueryStringSelector } from "selectors/queryString"
import { ONE_YEAR, getNextModeledDate, getStartDateWithBoundaries, getDatesAdjusted, isUnmodeledDate, computePeriodOrStartAndEndDates, computeLatestDate } from "helpers/dates"
import { getUnmodeledDates } from "selectors/dates"
import { getSelectedBase } from "./base"

export const getRawStartDate = createQueryStringSelector("startDate")
export const getRawEndDate = createQueryStringSelector("endDate")
export const getPeriod = createQueryStringSelector("period", ONE_YEAR)
export const getDate = createQueryStringSelector("date")

const getModelStartDate = state => get(state, "global.data.dates.data.model.availability.securitiesStartDate")
const getModelEndDate = state => get(state, "global.data.dates.data.model.availability.currentDate")
const getAssetStartDate = state => get(state, "global.data.assetDetails.data.availableFrom")
const getAssetEndDate = state => get(state, "global.data.assetDetails.data.availableTo")

export const getPeriodOrStartAndEndDates = createSelector(
  [getPeriod, getRawStartDate, getRawEndDate],
  computePeriodOrStartAndEndDates,
)

const getSelectedBaseStartDate = createSelector(
  [getSelectedBase],
  selectedBase => get(selectedBase, "availableFrom"),
)

export const getEarliestDate = createSelector(
  [getModelStartDate, getAssetStartDate, getSelectedBaseStartDate],
  (modelStartDate, assetStartDate, selectedBaseStartDate) => max([modelStartDate, assetStartDate, selectedBaseStartDate]),
)

export const getLatestDate = createSelector(
  [getModelEndDate, getAssetEndDate],
  computeLatestDate,
)

const computeEndDate = (date, latestDate, unmodeledDates) => {
  if (!date || date > latestDate) {
    return latestDate
  }
  if (isUnmodeledDate(date, unmodeledDates)) {
    return getNextModeledDate(date, latestDate, unmodeledDates)
  }
  return date
}

export const getAssetDetailsDate = createSelector(
  [getPeriodOrStartAndEndDates, getModelEndDate],
  (period, modelEndDate) => computeEndDate(period.endDate, modelEndDate),
)

export const getEndDate = createSelector(
  [getPeriodOrStartAndEndDates, getLatestDate, getUnmodeledDates],
  (period, latestDate, unmodeledDates) => computeEndDate(period.endDate, latestDate, unmodeledDates),
)

export const getStartDate = createSelector(
  [getEndDate, getPeriodOrStartAndEndDates, getEarliestDate, getLatestDate, getUnmodeledDates],
  getStartDateWithBoundaries,
)

export const getDriftDates = createSelector(
  [getEndDate, getEarliestDate],
  (endDate, earliestDate) => {
    const startDate = moment(endDate).subtract(3, "months").format("YYYY-MM-DD")
    let driftStartDate = null

    if (startDate < earliestDate) {
      driftStartDate = earliestDate
    } else {
      driftStartDate = startDate
    }

    return { startDate: driftStartDate, endDate }
  },
)

const getIsSelectedDateOutOfRange = createSelector(
  [getDate, getStartDate, getEndDate],
  (selectedDate, startDate, endDate) => {
    return selectedDate < startDate || selectedDate > endDate
  },
)

const getIsSelectedDateUnmodeled = createSelector(
  [getDate, getUnmodeledDates],
  (selectedDate, unmodeledDates) => isUnmodeledDate(selectedDate, unmodeledDates),
)

export const getIsSelectedDateAdjusted = createSelector(
  [getIsSelectedDateOutOfRange, getIsSelectedDateUnmodeled],
  (isOutOfRange, isUnmodeled) => {
    return isOutOfRange || isUnmodeled
  },
)

export const getIsPeriodAdjusted = createSelector(
  [getPeriodOrStartAndEndDates, getStartDate, getEndDate, getLatestDate],
  getDatesAdjusted,
)

export const getSelectedDate = createSelector(
  [getDate, getLatestDate, getEndDate, getIsSelectedDateOutOfRange, getIsSelectedDateUnmodeled, getUnmodeledDates],
  (selectedDate, latestDate, endDate, isOutOfRange, isUnmodeled, unmodeledDates) => {
    if (!selectedDate) return endDate
    if (isOutOfRange) return latestDate
    if (isUnmodeled) {
      return getNextModeledDate(selectedDate, latestDate, unmodeledDates)
    }
    return selectedDate
  },
)
